import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Mail, Link2, Copy, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const PLAN_LABELS = {
  trial: "Trial",
  starter: "Starter",
  growth: "Growth",
  enterprise: "Enterprise",
};

export default function InviteEarnPage() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allowedPlans, setAllowedPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [inviteMode, setInviteMode] = useState("email");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [generatedLink, setGeneratedLink] = useState("");

  const canContinue = useMemo(() => !!selectedPlan, [selectedPlan]);
  const canSendEmail = useMemo(() => !!selectedPlan && !!name.trim() && !!email.trim(), [selectedPlan, name, email]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.get("/referrals/config");
        const plans = Array.isArray(res?.allowed_plans) ? res.allowed_plans : [];
        if (!active) return;
        setAllowedPlans(plans);
        setSelectedPlan(res?.default_plan && plans.includes(res.default_plan) ? res.default_plan : plans[0] || "");
      } catch (err) {
        toast.error(err.message || "Davet ayarları yüklenemedi");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const sendEmailInvite = async () => {
    if (!canSendEmail) return;
    setSaving(true);
    try {
      const res = await api.post("/referrals/invite-email", {
        plan: selectedPlan,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
      });
      toast.success(res?.message || "Davet gönderildi");
    } catch (err) {
      toast.error(err.message || "Davet gönderilemedi");
    } finally {
      setSaving(false);
    }
  };

  const generateLink = async () => {
    if (!selectedPlan) return;
    setSaving(true);
    try {
      const res = await api.post("/referrals/generate-link", { plan: selectedPlan });
      const link = String(res?.invite_url || "");
      setGeneratedLink(link);
      if (link) {
        try {
          await navigator.clipboard.writeText(link);
          toast.success("Davet linki oluşturuldu ve kopyalandı");
        } catch {
          toast.success("Davet linki oluşturuldu");
        }
      }
    } catch (err) {
      toast.error(err.message || "Link üretilemedi");
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    if (!generatedLink) return;
    try {
      await navigator.clipboard.writeText(generatedLink);
      toast.success("Link kopyalandı");
    } catch {
      toast.error("Link kopyalanamadı");
    }
  };

  return (
    <div className="page-shell max-w-5xl">
      <div className="relative overflow-hidden rounded-3xl border border-border bg-[radial-gradient(circle_at_0%_0%,#e0f2fe,transparent_42%),radial-gradient(circle_at_100%_0%,#fef3c7,transparent_34%),#ffffff] p-6 sm:p-8">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-sky-200/40 blur-2xl" />
        <div className="absolute -left-10 -bottom-12 h-40 w-40 rounded-full bg-amber-200/40 blur-2xl" />
        <div className="absolute right-5 top-5 rounded-full bg-white/85 p-3 text-sky-600 shadow-sm ring-1 ring-border">
          <Sparkles className="h-7 w-7" />
        </div>

        <div className="relative">
          <h1 className="mt-3 text-2xl font-semibold text-foreground">Davet Et, Kazan</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Arkadaşınızı davet edin, ilk ödemesinde <span className="font-semibold text-sky-700">3 Ay kazan</span>.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="mt-4 rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">Yükleniyor...</div>
      ) : (
        <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_1.3fr]">
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="space-y-3">
              <div className={`rounded-xl border p-3 ${step === 1 ? "border-sky-300 bg-sky-50" : "border-border"}`}>
                <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-xs text-white">1</span>
                  Abonelik Seçimi
                </div>
              </div>
              <div className={`rounded-xl border p-3 ${step === 2 ? "border-sky-300 bg-sky-50" : "border-border"}`}>
                <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-xs text-white">2</span>
                  Arkadaş Daveti
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 sm:p-6">
            {step === 1 ? (
              <>
                <h2 className="text-lg font-semibold text-foreground">Abonelik Seçimi</h2>
                <p className="mt-1 text-sm text-muted-foreground">Davet için kullanılacak planı seçin.</p>
                <Label className="mt-4 block">Abonelik Planı</Label>
                <select
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  className="mt-2 h-11 w-full rounded-lg border border-border bg-card px-3 text-sm"
                >
                  {allowedPlans.map((p) => (
                    <option key={p} value={p}>
                      {PLAN_LABELS[p] || p}
                    </option>
                  ))}
                </select>
                <Button className="mt-5 h-11 w-full bg-sky-600 hover:bg-sky-500" disabled={!canContinue} onClick={() => setStep(2)}>
                  Devam Et
                </Button>
              </>
            ) : (
              <>
                <h2 className="text-lg font-semibold text-foreground">Arkadaş Daveti</h2>
                <p className="mt-1 text-sm text-muted-foreground">E-posta gönderin veya paylaşılabilir link üretin.</p>

                <div className="mt-4 grid grid-cols-2 gap-2 rounded-xl bg-muted/60 p-1">
                  <button
                    type="button"
                    className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm ${inviteMode === "email" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}
                    onClick={() => setInviteMode("email")}
                  >
                    <Mail className="h-4 w-4" /> E-posta
                  </button>
                  <button
                    type="button"
                    className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm ${inviteMode === "link" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}
                    onClick={() => setInviteMode("link")}
                  >
                    <Link2 className="h-4 w-4" /> Link
                  </button>
                </div>

                {inviteMode === "email" ? (
                  <div className="mt-4 grid grid-cols-1 gap-3">
                    <div>
                      <Label>İsim</Label>
                      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ad Soyad" />
                    </div>
                    <div>
                      <Label>Telefon</Label>
                      <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="05xx xxx xx xx" />
                    </div>
                    <div>
                      <Label>E-posta</Label>
                      <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ornek@firma.com" />
                    </div>
                    <Button className="h-11 bg-sky-600 hover:bg-sky-500" disabled={!canSendEmail || saving} onClick={sendEmailInvite}>
                      {saving ? "Gönderiliyor..." : "Davet Gönder"}
                    </Button>
                  </div>
                ) : (
                  <div className="mt-4 space-y-3">
                    <Button className="h-11 bg-sky-600 hover:bg-sky-500" disabled={!selectedPlan || saving} onClick={generateLink}>
                      {saving ? "Üretiliyor..." : "Davet Linki Üret"}
                    </Button>
                    {generatedLink ? (
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                        <div className="mb-2 inline-flex items-center gap-1 text-xs text-emerald-700">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Link hazır
                        </div>
                        <div className="break-all rounded-lg border border-emerald-200 bg-card px-3 py-2 text-sm text-foreground/80">
                          {generatedLink}
                        </div>
                        <Button variant="outline" className="mt-2" onClick={copyLink}>
                          <Copy className="mr-2 h-4 w-4" />
                          Linki Kopyala
                        </Button>
                      </div>
                    ) : null}
                  </div>
                )}

                <Button variant="ghost" className="mt-3" onClick={() => setStep(1)}>
                  Abonelik seçimine dön
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
